import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Download, AlertCircle, ShieldCheck, Sparkles, Mail, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cookies are HttpOnly + cross-site; axios needs `withCredentials` to ship
// them on the public API. The backend's CORS allows our origin.
const PUBLIC_AXIOS = axios.create({ baseURL: API_BASE, withCredentials: true });

const LEVEL_COLOR = {
  critical: { bg: "bg-rose-50",   ring: "ring-rose-200",   text: "text-rose-700" },
  high:     { bg: "bg-orange-50", ring: "ring-orange-200", text: "text-orange-700" },
  medium:   { bg: "bg-amber-50",  ring: "ring-amber-200",  text: "text-amber-700" },
  low:      { bg: "bg-emerald-50",ring: "ring-emerald-200",text: "text-emerald-700" },
};

const VERDICT = {
  critical: "Do not sign — fix critical risks first",
  high:     "Hold — material risks require redlines",
  medium:   "Sign with edits — minor tightening recommended",
  low:      "Safe to sign — drafting hygiene only",
};

/**
 * PublicScorecard — landing page for shared scorecard links.
 *
 * No auth required. Reachable at /scorecard/:token. Fetches lightweight
 * metadata first so the header renders instantly, then loads the full PDF
 * in an <iframe>. If the link is expired / revoked / unknown we show a
 * tailored empty state pointing the visitor at AgentFlow's homepage.
 */
export default function PublicScorecard() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMeta = async () => {
    setLoading(true);
    try {
      const { data } = await PUBLIC_AXIOS.get(`/public/scorecard/${token}`);
      setMeta(data);
      setError(null);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setError({
        status,
        message: typeof detail === "string"
          ? detail
          : (status === 404 ? "This link is invalid." : "This link has expired or been revoked."),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await fetchMeta();
    })();
    return () => { cancelled = true; };
  }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-32 text-slate-500 gap-2">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading scorecard…
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div className="mx-auto max-w-md text-center py-16" data-testid="public-share-error">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-100 text-rose-600">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h1 className="font-display text-2xl font-semibold text-slate-900 mt-5">
            {error.status === 404 ? "Link not found" : "Link no longer active"}
          </h1>
          <p className="mt-2 text-slate-600">
            {error.message}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            Ask the person who sent you this link to create a new one.
          </p>
          <a
            href="/"
            className="inline-block mt-6 text-sm font-semibold text-blue-700 hover:text-blue-800"
            data-testid="public-share-home-link"
          >
            Learn more about AgentFlow →
          </a>
        </div>
      </Shell>
    );
  }

  // Email gate — show the capture form before any scorecard content is
  // revealed. The viewer_email field is echoed from the backend after a
  // valid HMAC cookie was sent, so subsequent visits skip this screen.
  if (meta.require_email && !meta.viewer_email) {
    return (
      <Shell>
        <EmailGate
          token={token}
          documentName={meta.document_name}
          workspaceName={meta.workspace_name}
          onIdentified={fetchMeta}
        />
      </Shell>
    );
  }

  const level = (meta.level || "medium").toLowerCase();
  const palette = LEVEL_COLOR[level] || LEVEL_COLOR.medium;

  return (
    <Shell>
      <div className="mx-auto max-w-4xl py-8" data-testid="public-share-view">
        {/* Hero --------------------------------------------------------- */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-6 sm:px-8 py-6 border-b border-slate-100">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
              Compliance Scorecard
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mt-1 leading-tight">
              {meta.document_name}
            </h1>
            <div className="text-sm text-slate-500 mt-1">
              Prepared by <span className="font-medium text-slate-900">{meta.workspace_name}</span>
              {meta.expires_at ? (
                <> · Link expires {new Date(meta.expires_at).toLocaleDateString()}</>
              ) : null}
            </div>
          </div>

          <div className={`px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-5 ${palette.bg}`}>
            <div className={`inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-white ring-4 ${palette.ring} shrink-0`}>
              <div className="text-center leading-none">
                <div className="text-3xl font-bold text-slate-900">{meta.score}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">/ 100</div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-xs uppercase tracking-wider font-semibold ${palette.text}`}>
                Safe to sign?
              </div>
              <div className="font-display text-lg sm:text-xl font-semibold text-slate-900 mt-1 leading-snug">
                {VERDICT[level] || VERDICT.medium}
              </div>
              <div className="text-sm text-slate-600 mt-2 leading-relaxed">
                {meta.headline}
              </div>
            </div>
            <a
              href={meta.pdf_url}
              download
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 hover:bg-blue-700 text-white px-4 h-10 text-sm font-semibold shrink-0"
              data-testid="public-share-download"
            >
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </a>
          </div>
        </div>

        {/* PDF preview -------------------------------------------------- */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <iframe
            title="Compliance Scorecard"
            src={meta.pdf_url}
            className="w-full h-[900px] bg-slate-50"
            data-testid="public-share-pdf-iframe"
          />
        </div>

        {/* Footer -------------------------------------------------------- */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 flex items-start gap-3">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-slate-900 mb-0.5">Confidential</div>
            This scorecard summarises an AI-generated risk analysis and is <strong>not legal advice</strong>.
            Have a qualified attorney review the underlying document before signing.
            Share only with parties bound to confidentiality.
          </div>
        </div>

        <CrossSell />
      </div>
    </Shell>
  );
}

function EmailGate({ token, documentName, workspaceName, onIdentified }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      await PUBLIC_AXIOS.post(`/public/scorecard/${token}/identify`, { email: email.trim() });
      await onIdentified();
    } catch (e2) {
      const status = e2?.response?.status;
      const detail = e2?.response?.data?.detail;
      // Pydantic returns an array of validation errors on 422 — pull a
      // single friendly message rather than dumping JSON at the user.
      if (status === 422) {
        setErr("Please enter a valid email address.");
      } else if (typeof detail === "string") {
        setErr(detail);
      } else {
        setErr("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-md py-12" data-testid="public-share-email-gate">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-white">
          <Lock className="h-5 w-5" />
        </div>
        <h1 className="font-display text-2xl font-semibold text-slate-900 mt-5 leading-tight">
          Enter your email to view this scorecard
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          <span className="font-medium text-slate-900">{workspaceName}</span> shared a
          Compliance Scorecard{documentName ? <> for <span className="font-medium text-slate-900">{documentName}</span></> : null}.
          Your email helps them know who's reviewed it.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <div>
            <label htmlFor="viewer-email" className="block text-xs font-semibold text-slate-700 mb-1">
              Your work email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <input
                id="viewer-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-500"
                data-testid="public-share-email-input"
              />
            </div>
            {err ? (
              <div className="mt-2 text-xs text-rose-600 flex items-center gap-1.5" data-testid="public-share-email-error">
                <AlertCircle className="h-3.5 w-3.5" /> {err}
              </div>
            ) : null}
          </div>
          <Button
            type="submit"
            disabled={submitting || !email.trim()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10"
            data-testid="public-share-email-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            View scorecard
          </Button>
        </form>

        <p className="mt-5 text-[11px] text-slate-500 leading-relaxed">
          We don't send you anything. {workspaceName} sees your email so they know which
          recipients reviewed the document.
        </p>
      </div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-5xl px-6 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="public-share-brand">
            <img
              src="/agentflow-logo-512.png"
              alt="AgentFlow"
              width="32"
              height="32"
              className="h-8 w-8 rounded-lg"
            />
            <span className="font-display font-semibold text-slate-900">AgentFlow</span>
          </a>
          <a
            href="/"
            className="text-sm text-slate-600 hover:text-slate-900 hidden sm:inline"
          >
            Learn more →
          </a>
        </div>
      </header>
      <main className="px-4 sm:px-6">{children}</main>
    </div>
  );
}

function CrossSell() {
  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600/20 text-blue-300 shrink-0">
        <Sparkles className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">
          Want this for your own contracts?
        </div>
        <div className="text-xs text-slate-300 mt-1">
          AgentFlow turns any contract into a one-page risk scorecard in under 60 seconds.
        </div>
      </div>
      <Button asChild className="bg-blue-600 hover:bg-blue-500 text-white shrink-0">
        <a href="/" data-testid="public-share-cta">
          <ShieldCheck className="h-4 w-4 mr-2" /> Try AgentFlow free
        </a>
      </Button>
    </div>
  );
}
